import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useAuth } from '../../AuthContext';
import useCompanyAccess, { refreshCompanyAccess } from '../../hooks/company/useCompanyAccess';
import { readLastKnownAccess } from '../../utils/company/lastKnownAccessLevel';
import { companyAccessSuspendedScreen } from '../../utils/company/companyAccessCopy';
import AccessStatusScreen from '../auth/AccessStatusScreen';
import AccessStatusBackdrop from '../auth/AccessStatusBackdrop';
import useAccessWaitGaveUp from '../../hooks/company/useAccessWaitGaveUp';
import UnpaidBalanceNotice from '../admin/company/plan/billing/UnpaidBalanceNotice';
import { CONTACT_EMAIL, CONTACT_MAILTO } from '../../config/contactInfo';
import LoadErrorState from '../common/LoadErrorState';
import useBillingStatus from '../../hooks/company/useBillingStatus';
import useBillingActions from '../../hooks/company/useBillingActions';
import { useSuppressAiFab } from '../../contexts/AiFabSuppressionContext';
import { billingActions } from '../../utils/billing/billingState';
import { isHealthyButLocked } from '../../utils/billing/healthyButLocked';
import useLockedBillingSelfHeal from '../../hooks/company/useLockedBillingSelfHeal';
import { suspendedLockBody } from '../admin/company/plan/billing/stripeLockCopy';
import { lazyWithRetry as lazy } from '../../utils/ui/lazyWithRetry';
import { setReactivationInProgress } from '../../utils/billing/reactivationInProgress';
import { removeBootReturnCover } from '../../utils/billing/bootReturnCover';
import { prefetchRoleHome } from '../../utils/ui/prefetchRoleHome';
import useReactivationWait from '../../hooks/company/useReactivationWait';
import { takeCheckoutParam } from '../../hooks/company/useCheckoutReturn';
import { markPortalStarted, takePortalReturn } from '../../utils/billing/portalReturnMarker';
import { clearSubscribePending } from '../../utils/billing/subscribeCheckoutMarker';
import { pushToast } from '../common/toast/toastStore';
import { getMyBillingStatus } from '../../services/billing/billingApi';

// Only an owner/admin of a suspended company ever opens it, so it stays out of the entry chunk.
const PlanChooserModal = lazy(() => import('../admin/company/plan/billing/PlanChooserModal'));
const StripeHandoffModal = lazy(() => import('../admin/company/plan/billing/StripeHandoffModal'));
// KAN-866: the confirming card + celebration — owner/admin only, so also out of the entry chunk (preloaded for them below).
const loadReactivation = () => import('./SuspendedReactivation');
const SuspendedReactivation = lazy(loadReactivation);

// KAN-855: literal ids (never built from a template) so the i18n guards see every key.
const ASK_COPY = {
  subscribe: ['companyAccess.screen.bodyAdminAsk.subscribe', 'Ask {owner} to subscribe to start using CrewVex again.', 'companyAccess.screen.bodyAdminAsk.subscribeNoName', 'Ask the owner to subscribe to start using CrewVex again.'],
  resubscribe: ['companyAccess.screen.bodyAdminAsk.resubscribe', 'Ask {owner} to resubscribe to start using CrewVex again.', 'companyAccess.screen.bodyAdminAsk.resubscribeNoName', 'Ask the owner to resubscribe to start using CrewVex again.'],
};

/**
 * KAN-204 Ship B — a SUSPENDED company's people never reach the app. ProtectedRoute
 * renders this around its children once every earlier gate (2FA, deactivated
 * account / company) has passed:
 *
 *   - worker, supervisor, billing, operations: the suspended screen — what
 *     happened, "ask the owner", Sign out. No shell behind it.
 *   - owner + admins: the same screen, but "Email us to subscribe / pay" is its ONE blue
 *     button and Sign out goes quiet (outlined) — no card inside the card. KAN-196: that button
 *     is the real action — Update payment (Stripe portal) / Resubscribe / Subscribe (plan chooser);
 *     when the status can't be read it shows the load error with retry.
 *
 * Live both ways: `useCompanyAccess` is the one store (realtime ping + refusal
 * refetch + resume), so the screen replaces the app within ~2s of the flip and
 * the app returns the moment it lifts, without a reload. While the level is
 * still loading it reads as `full` (today's behavior) EXCEPT on a cold start
 * where this device remembers THIS user + company as suspended
 * (`lastKnownAccessLevel`, level + reason only, cleared at sign-out): the
 * screen shows at the first render while the fresh read confirms, and any
 * fresh answer other than suspended returns the app. No remembered state, or
 * another person's, is never used — nobody else sees a wait or a change.
 *
 * Client UX only. The server already refuses every write for a suspended
 * company; nothing here is an authorization decision. Platform super-admins are
 * not wrapped (ProtectedRoute skips them) and /platform/* never reaches it.
 */
export default function SuspendedAccessGate({ children, email = null, onSignOut }) {
  const { sessionUser, tenantId, role: homeRole } = useAuth();
  const { level, access } = useCompanyAccess();
  const userId = sessionUser?.id;
  const remembered = useMemo(() => readLastKnownAccess(userId, tenantId), [userId, tenantId]);
  // No fresh answer yet (loading, or the read failed): fall back to what this device last knew.
  const rememberedSuspended = !access && remembered?.level === 'suspended';
  const locked = level === 'suspended' || rememberedSuspended;
  // KAN-866: the reactivation wait lives HERE so it survives the lift (this gate returns `children` the moment the
  // lock ends, which would unmount a card still mid-beat). `session` = { status } while one is alive.
  const [session, setSession] = useState(null);
  const reloadRef = useRef(null);
  // Each wait poll also re-reads the access level itself: the lift never depends on the realtime ping alone.
  const onPoll = useCallback(() => { reloadRef.current?.(); void refreshCompanyAccess(tenantId); }, [tenantId]);
  const wait = useReactivationWait({ lifted: !locked, onPoll });
  const { start } = wait;
  const begin = useCallback((kind, status) => {
    setReactivationInProgress(true); // before the lift mounts the app tree (App.js's boot splash trigger reads it)
    setSession({ status });
    start(kind);
  }, [start]);
  const endSession = useCallback(() => { setReactivationInProgress(false); setSession(null); }, []);
  const keepStatus = useCallback((status) => setSession((s) => (s ? { ...s, status } : s)), []);
  // The body (and its billing reload) unmounts at the lift: re-read once so the celebration names the plan now live.
  const hasSession = Boolean(session);
  // KAN-877: order is suspended card -> Confirming -> celebration -> app. Warm the role home so the lift never suspends.
  useEffect(() => { if (hasSession) prefetchRoleHome(homeRole); }, [hasSession, homeRole]);
  useEffect(() => () => setReactivationInProgress(false), []);
  const activated = !locked && hasSession && wait.phase === 'activated';
  useEffect(() => {
    if (!activated) return undefined;
    let alive = true;
    Promise.resolve(getMyBillingStatus()).then((s) => { if (alive && s) keepStatus(s); }, () => {});
    return () => { alive = false; };
  }, [activated, keepStatus]);
  const reactivation = session ? (
    <Suspense fallback={null}><SuspendedReactivation wait={wait} status={session.status} onEnd={endSession} /></Suspense>
  ) : null;
  if (!locked) return reactivation ? <>{children}{reactivation}</> : children;
  return (
    <>
      <SuspendedScreen
        reason={access?.reason ?? remembered?.reason}
        email={email}
        onSignOut={onSignOut}
        begin={begin}
        keepStatus={keepStatus}
        reloadRef={reloadRef}
      />
      {reactivation}
    </>
  );
}

/**
 * Mounted only for a suspended company. Holds the empty frame until the role is known and, for
 * owners/admins, until their billing status loaded or failed (KAN-865), so the card appears once,
 * complete. Capped at 3s: a slow read never strands anyone on a blank screen.
 */
function SuspendedScreen({ reason, email, onSignOut, begin, keepStatus, reloadRef }) {
  const { reload: reloadAccess } = useCompanyAccess();
  const { role } = useAuth();
  // Disabled for every other role (returns loading + null forever), so only an admin waits on it.
  const billingState = useBillingStatus();
  const waiting = !role || (role === 'admin' && !billingState.status && billingState.loadState === 'loading');
  // KAN-867: locked but the subscription is healthy: re-check with Stripe once (and hold the frame meanwhile).
  const healthyLocked = role === 'admin' && billingState.status?.billingAvailable === true
    && isHealthyButLocked({ level: 'suspended', stripeLinked: billingState.status.stripeLinked, base: billingState.billing?.base });
  const { checking } = useLockedBillingSelfHeal({ active: healthyLocked, reload: billingState.reload, reloadAccess });
  const gaveUp = useAccessWaitGaveUp(waiting || checking ? 'loading' : 'ready');
  if (checking && !gaveUp) return <AccessStatusBackdrop />;
  if (waiting && !gaveUp) return <AccessStatusBackdrop />;
  return <SuspendedScreenBody reason={reason} email={email} onSignOut={onSignOut} role={role} billingState={billingState} begin={begin} keepStatus={keepStatus} reloadRef={reloadRef} />;
}

function SuspendedScreenBody({ reason, email, onSignOut, role, billingState, begin, keepStatus, reloadRef }) {
  const intl = useIntl();
  const { sessionUser, tenantId } = useAuth();
  const userId = sessionUser?.id;
  // The floating AI button has no place on the suspended screen (mounted only while it shows).
  useSuppressAiFab();
  const isAdmin = role === 'admin';
  const copy = companyAccessSuspendedScreen(reason, role);
  const { status, billing, isOwner, reload, loadState } = billingState;
  const ctl = useBillingActions({ status, reload });
  useSuspendedReactivationTriggers({ isAdmin, userId, tenantId, status, begin, keepStatus, reloadRef, reload, intl });
  // The "left for Stripe" marker is written only once the hand-off actually starts (a failed portal open never marks).
  const portalTapped = useRef(false);
  const portalTap = useCallback(() => { portalTapped.current = true; return ctl.portal(); }, [ctl]);
  useEffect(() => {
    if (ctl.handoff !== 'portal' || !portalTapped.current) return;
    portalTapped.current = false;
    markPortalStarted(userId, tenantId);
  }, [ctl.handoff, userId, tenantId]);
  const available = status?.billingAvailable === true;
  const acts = isAdmin && status && available
    ? billingActions({ base: billing.base, isOwner, isAdmin: true, stripeLinked: status.stripeLinked })
    : null;
  const kind = acts ? acts.primary : null;
  // An ownerless company has nobody to "ask": it falls through to the support email.
  const hasOwner = status?.hasOwner !== false;
  const linked = status?.stripeLinked === true;
  // KAN-867: Stripe-linked manage / resume while locked is the healthy-but-locked mismatch (self-heal, then Email us below); no pay action.
  const action = kind === 'updatePayment' && linked ? 'portal'
    : (kind === 'subscribe' || kind === 'resubscribe' || (kind === 'updatePayment' && !linked)) ? 'chooser' : null;
  const labelId = kind === 'updatePayment' && action === 'portal' ? ['billing.action.updatePayment', 'Update payment']
    : kind === 'resubscribe' ? ['billing.action.resubscribe', 'Resubscribe']
      : ['billing.action.subscribe', 'Subscribe'];
  // KAN-855: a regular admin never subscribes / resubscribes: the screen says who can.
  const askVerb = acts && hasOwner && !action && typeof acts.ownerOnlyNote === 'string' ? acts.ownerOnlyNote : null;
  // KAN-855: billing is available but no pay action fits this viewer (e.g. a company marked "Paying by hand",
  // then locked): an owner/admin never sees only Sign out, they get the support email.
  const noFit = Boolean(acts) && !action && !askVerb;
  let primaryAction;
  if (isAdmin && action) {
    primaryAction = {
      label: intl.formatMessage({ id: labelId[0], defaultMessage: labelId[1] }),
      onClick: action === 'portal' ? portalTap : ctl.openSubscribe,
      busy: Boolean(ctl.busy || ctl.handoff),
      busyLabel: intl.formatMessage({ id: 'billing.handoff.opening', defaultMessage: 'Opening secure page…' }),
      testId: 'suspended-billing-action',
    };
  }
  if (isAdmin && status && !available) {
    // Live billing is not switched on: the previous contact path, exactly as before Stripe.
    primaryAction = {
      label: intl.formatMessage({ id: `company.plan.billing.emailBtn.${copy.verb}`, defaultMessage: copy.verb === 'pay' ? 'Email us to pay' : 'Email us to subscribe' }),
      href: CONTACT_MAILTO,
      testId: 'suspended-email-us',
    };
  }
  if (noFit) {
    primaryAction = {
      label: intl.formatMessage({ id: 'billing.action.emailUs', defaultMessage: 'Email us' }),
      href: CONTACT_MAILTO,
      address: CONTACT_EMAIL,
      testId: 'suspended-email-us',
    };
  }
  // Billing available: the approved prototype copy (the default screen copy stays for every other caller).
  const stripeCopy = isAdmin && status && available;
  const suspendedTitle = stripeCopy ? intl.formatMessage({ id: 'billing.lock.suspended.headline', defaultMessage: 'Account suspended' }) : undefined;
  // KAN-865: with a pay button showing, the sentence names that button's action.
  const bodyMsg = stripeCopy ? suspendedLockBody({ action, kind }) : null;
  let suspendedBody = bodyMsg
    ? intl.formatMessage({ id: bodyMsg.id, defaultMessage: bodyMsg.defaultMessage })
    : stripeCopy && billing.base === 'past_due'
      ? intl.formatMessage({ id: 'billing.lock.suspended.pastDue', defaultMessage: 'Payment has been overdue for a while, so access is paused. Update payment to turn everything back on.' })
      : intl.formatMessage(copy.body);
  if (askVerb) {
    const name = status.ownerName || null;
    const ask = ASK_COPY[askVerb] || ASK_COPY.subscribe;
    suspendedBody = name
      ? intl.formatMessage({ id: ask[0], defaultMessage: ask[1] }, { owner: name })
      : intl.formatMessage({ id: ask[2], defaultMessage: ask[3] });
  } else if (noFit) {
    suspendedBody = intl.formatMessage({ id: 'billing.lock.suspended.noAction', defaultMessage: "We can't open payment for this account from here. Email us and we'll get you back in." });
  }
  // KAN-867: locked while the subscription is healthy: paying can't unlock it, so support is the way forward.
  const mismatch = isAdmin && Boolean(status) && available
    && isHealthyButLocked({ level: 'suspended', stripeLinked: linked, base: billing.base });
  if (mismatch) {
    primaryAction = {
      label: intl.formatMessage({ id: 'billing.action.emailUs', defaultMessage: 'Email us' }),
      href: CONTACT_MAILTO,
      address: CONTACT_EMAIL,
      testId: 'suspended-email-us',
    };
    suspendedBody = intl.formatMessage({ id: 'billing.lock.suspended.mismatch', defaultMessage: 'Your subscription is active, but your account is still paused. Email us and we\'ll sort it out.' });
  }
  const statusError = isAdmin && !status && loadState === 'error';
  return (
    <>
      <AccessStatusScreen
        variant="suspended"
        email={email}
        bodyOverride={suspendedBody}
        titleOverride={suspendedTitle}
        primaryAction={primaryAction}
        onSignOut={onSignOut}
      >
        {ctl.unpaid || statusError ? (
          <>
            <UnpaidBalanceNotice unpaid={ctl.unpaid} onPay={ctl.payOpenBill} onDismiss={ctl.clearUnpaid} touch />
            {statusError ? <LoadErrorState variant="inline" onRetry={reload} testId="suspended-billing-error" /> : null}
          </>
        ) : null}
      </AccessStatusScreen>
      <Suspense fallback={null}><StripeHandoffModal open={Boolean(ctl.handoff)} kind={ctl.handoff || 'portal'} /></Suspense>
      {ctl.chooser && status ? (
        <Suspense fallback={null}>
          <PlanChooserModal mode="subscribe" status={status} onClose={ctl.closeChooser} onDone={reload} onRetry={reload} onUnpaid={ctl.showUnpaid} onHandoff={ctl.leaveFromChooser} />
        </Suspense>
      ) : null}
    </>
  );
}

/** KAN-866: the two return paths read on mount (Stripe checkout `?checkout=`, portal marker) + the live reload / status feed for the gate-held wait. */
function useSuspendedReactivationTriggers({ isAdmin, userId, tenantId, status, begin, keepStatus, reloadRef, reload, intl }) {
  const took = useRef(false);
  useEffect(() => { if (isAdmin) loadReactivation().catch(() => {}); }, [isAdmin]); // warm the chunk before any trigger
  useEffect(() => { reloadRef.current = reload; }, [reloadRef, reload]);
  useEffect(() => { if (status) keepStatus(status); }, [keepStatus, status]);
  useEffect(() => {
    if (!isAdmin || took.current) return;
    took.current = true;
    const param = takeCheckoutParam();
    const portal = takePortalReturn(userId, tenantId);
    if (param === 'done') begin('checkout', status);
    else if (param === 'cancelled') {
      removeBootReturnCover();
      clearSubscribePending(userId, tenantId); // an abandoned checkout must not celebrate a later, unrelated subscription
      pushToast({ type: 'info', message: intl.formatMessage({ id: 'billing.toast.abandoned', defaultMessage: "Checkout wasn't completed. Nothing was charged." }) });
    } else if (portal) begin('portal', status);
  }, [isAdmin, userId, tenantId, status, begin, intl]);
}

SuspendedScreenBody.propTypes = {
  begin: PropTypes.func.isRequired,
  keepStatus: PropTypes.func.isRequired,
  reloadRef: PropTypes.shape({}).isRequired,
  reason: PropTypes.string,
  email: PropTypes.string,
  onSignOut: PropTypes.func.isRequired,
  role: PropTypes.string,
  billingState: PropTypes.shape({
    status: PropTypes.object,
    billing: PropTypes.object,
    isOwner: PropTypes.bool,
    reload: PropTypes.func,
    loadState: PropTypes.string,
  }).isRequired,
};

SuspendedScreen.propTypes = {
  begin: PropTypes.func.isRequired,
  keepStatus: PropTypes.func.isRequired,
  reloadRef: PropTypes.shape({}).isRequired,
  reason: PropTypes.string,
  email: PropTypes.string,
  onSignOut: PropTypes.func.isRequired,
};

SuspendedAccessGate.propTypes = {
  children: PropTypes.node,
  email: PropTypes.string,
  onSignOut: PropTypes.func.isRequired,
};
